
























































import { Component, Prop, Vue, Watch, Ref } from "vue-property-decorator";
import MultiLevelObject from "@/interfaces/MultiLevelObject";
import MultiLevelDropdownV2 from "./MultiLevelDropdownV2.vue";

interface SearchItem {
  option: MultiLevelObject;
  level: number;
}

const DEFAULT_PLACEHOLDER = "Selezionare una voce";

export interface MultiLevelMultipleObject {
  [key: number]: MultiLevelObject[];
}

@Component({
  name: "MultiLevelSelectV2",
  components: {
    MultiLevelDropdownV2,
  },
})
export default class MultiLevelSelectV2 extends Vue {
  @Prop() readonly value!: string | Array<string[]> | null;
  @Prop({ default: DEFAULT_PLACEHOLDER }) readonly placeholder?: string;
  @Prop({
    default: () => [],
  })
  readonly headers?: Array<string>;
  @Prop({
    default: () => [],
  })
  readonly options!: Array<MultiLevelObject>;
  @Prop({ default: false }) readonly multiple?: boolean;
  @Prop({ default: 0 }) readonly level!: number;
  @Prop({ type: Boolean, default: false }) readonly small!: boolean;
  @Prop({ default: false }) readonly hasAlignRight!: boolean;
  @Prop({ type: Boolean, default: false }) readonly searchEnabled!: boolean;
  @Prop({ default: null }) iconSet?: string;

  @Ref("dropdownChild") readonly dropdownChild!: MultiLevelDropdownV2;

  @Watch("value")
  onValueChange(value: string | Array<string[]> | null) {
    this.updateSelected(value);
  }

  @Watch("options")
  onOptionsChanged(value: MultiLevelObject[]) {
    this.items = [...value];
    this.updateSelected(this.value);
  }

  created() {
    this.items = [...this.options];
    this.updateSelected(this.value);
  }

  visible = false;
  items: MultiLevelObject[] = [];
  selected: MultiLevelObject | null = null;
  selectedLevel = this.level;
  selectedMultiple: MultiLevelMultipleObject = {};

  get label(): string {
    if (this.multiple) {
      let first: MultiLevelObject | null = null;
      let count = 0;

      for (const level in this.selectedMultiple) {
        if (
          first == null &&
          this.selectedMultiple[level] &&
          this.selectedMultiple[level].length
        ) {
          first = this.selectedMultiple[level][0];
        }

        count += this.selectedMultiple[level].length;
      }

      if (count && first) {
        let label = first.text;

        if (count > 1) {
          label += ` + altre ${count - 1}`;
        }

        return label;
      } else {
        return this.placeholder || DEFAULT_PLACEHOLDER;
      }
    } else {
      if (this.selected == null) {
        return this.placeholder || DEFAULT_PLACEHOLDER;
      }

      return this.selected.text;
    }
  }

  toggleDropdown() {
    this.visible = !this.visible;
    this.dropdownChild.focusSearch();
  }

  closeDropdown() {
    if (this.visible) {
      this.visible = false;
    }
  }

  /**
   * Finds an option by its ID, ensuring it sits at the exact target level.
   * @param options Array of MultiLevelObject to search
   * @param id The ID to find
   * @param targetLevel The required depth level of the item
   */
  findOptionByIdAndLevel(
    options: MultiLevelObject[],
    id: string,
    targetLevel: number
  ): MultiLevelObject | null {
    const stack: SearchItem[] = [];

    for (let i = options.length - 1; i >= 0; i--) {
      stack.push({ option: options[i], level: 0 });
    }

    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) continue;

      const { option, level } = current;

      if (option.id == id && level === targetLevel) {
        return option;
      }

      if (
        option.children &&
        option.children.length > 0 &&
        level < targetLevel
      ) {
        for (let i = option.children.length - 1; i >= 0; i--) {
          stack.push({
            option: option.children[i],
            level: level + 1,
          });
        }
      }
    }

    return null;
  }

  updateSelected(value: string | string[][] | null) {
    if (this.multiple && Array.isArray(value)) {
      this.selectedMultiple = {};
      for (let i = 0; i < (value as string[][]).length; i++) {
        this.selectedMultiple[i] = [];
      }
      this.updateSelectedMultiple(this.items, 0);
      this.syncPartialStates(this.items);
    } else {
      if (value && this.items) {
        this.clearOptionSelection(this.items);

        const foundOption = this.findOptionByIdAndLevel(
          this.items,
          value as string,
          this.selectedLevel
        );

        if (foundOption) {
          this.$set(foundOption, "selected", true);
          this.selected = foundOption;
        } else {
          this.selected = null;
        }
      }
    }
  }

  syncPartialStates(options: MultiLevelObject[]) {
    for (const option of options) {
      if (option.children && option.children.length > 0) {
        // Recurse to the deepest level
        this.syncPartialStates(option.children);

        let selectedCount = 0;
        let partialCount = 0;
        const totalChildren = option.children.length;

        for (const child of option.children) {
          if (child.selected) selectedCount++;
          if (child.partial) partialCount++;
        }

        if (selectedCount === 0) {
          this.$set(option, "selected", false);
          this.$set(option, "partial", partialCount > 0);
        } else if (selectedCount < totalChildren) {
          this.$set(option, "selected", false);
          this.$set(option, "partial", true);
        } else {
          this.$set(option, "selected", true);
          this.$set(option, "partial", false);
        }
      }
    }
  }

  updateSelectedMultiple(
    options: MultiLevelObject[],
    level: number,
    parent: MultiLevelObject | null = null
  ) {
    const selectedIdsAtLevel =
      this.value && Array.isArray(this.value) && this.value[level]
        ? this.value[level]
        : [];

    if (!this.selectedMultiple[level]) {
      this.$set(this.selectedMultiple, level, []);
    }

    options.forEach((option) => {
      let isSelected = selectedIdsAtLevel.includes(option.id);

      if (!isSelected && parent && parent.selected) {
        isSelected = true;
      }

      this.$set(option, "selected", isSelected);

      if (option.selected) {
        this.selectedMultiple[level].push(option);

        if (parent && !parent.selected) {
          this.$set(parent, "partial", true);
        }
      }

      if (option.children && option.children.length) {
        this.updateSelectedMultiple(option.children, level + 1, option);
      }
    });
  }

  processMultipleOptions(options: MultiLevelObject[], level = 0) {
    if (!this.selectedMultiple[level]) {
      this.selectedMultiple[level] = [];
    }

    options.forEach((option) => {
      if (option.selected) {
        this.selectedMultiple[level].push(option);
      }

      if (option.partial && option.children) {
        this.processMultipleOptions(option.children, level + 1);
      }
    });
  }

  emitMultipleValue() {
    const keys = Object.keys(this.selectedMultiple).map(Number);
    const length = keys.length > 0 ? Math.max(...keys) + 1 : 0;

    const value: string[][] = Array.from({ length }, () => []);

    for (let i = 0; i < length; i++) {
      if (this.selectedMultiple[i]) {
        value[i] = this.selectedMultiple[i].map((element) => element.id);
      }
    }

    this.$emit("input", value);
  }

  onApply() {
    this.emitMultipleValue();
    this.closeDropdown();
  }

  onSelected(option: MultiLevelObject, level: number) {
    if (this.multiple) {
      if (this.items) {
        this.selectedMultiple = {};
        this.processMultipleOptions(this.items, 0);
      }
    } else {
      if (this.items) {
        this.clearOptionSelection(this.items);
      }

      this.$set(option, "selected", true);
      this.closeDropdown();
      this.selected = option;

      this.$emit("input", option.id);
    }

    this.selectedLevel = level;
    this.$emit("selected", option, level);
  }

  clearOptionSelection(options: MultiLevelObject[]) {
    options.forEach((option) => {
      if (option.selected) {
        this.$set(option, "selected", false);
      }
      if (option.partial) {
        this.$set(option, "partial", false);
      }
      if (option.children && option.children.length) {
        this.clearOptionSelection(option.children);
      }
    });
  }

  onReset() {
    if (this.items && this.items.length > 0) {
      this.clearOptionSelection(this.items);
    }
    this.selected = null;
    this.selectedMultiple = {};

    if (this.dropdownChild) {
      this.dropdownChild.closeAllAccordionsOptions();
    }

    if (this.multiple) {
      this.$emit("input", []);
    } else {
      this.$emit("input", null);
    }
  }
}
